import { useState, useEffect } from 'react';

export type LocationPermissionStatus = 'unknown' | 'prompt' | 'granted' | 'denied';

export const useLocationPermission = () => {
  const [permissionStatus, setPermissionStatus] = useState<LocationPermissionStatus>('unknown');
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check current permission status
  const checkPermission = async () => {
    if (!navigator.geolocation) {
      setPermissionStatus('denied');
      setError('Geolocation is not supported by your browser');
      return;
    }

    // Check if Permissions API is available
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionStatus(result.state as LocationPermissionStatus);
        
        // Listen for permission changes
        result.onchange = () => {
          setPermissionStatus(result.state as LocationPermissionStatus);
        };
      } catch (err) {
        // Permissions API not supported, try to get position to check
        try {
          await new Promise<void>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              () => {
                setPermissionStatus('granted');
                resolve();
              },
              (error) => {
                if (error.code === error.PERMISSION_DENIED) {
                  setPermissionStatus('denied');
                } else {
                  setPermissionStatus('prompt');
                }
                resolve();
              },
              { timeout: 1000 }
            );
          });
        } catch (e) {
          setPermissionStatus('prompt');
        }
      }
    } else {
      // Permissions API not available, assume prompt state
      setPermissionStatus('prompt');
    }
  };

  // Request location permission
  const requestPermission = async (): Promise<boolean> => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setPermissionStatus('denied');
      return false;
    }

    setIsRequesting(true);
    setError(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 0 // Force fresh location
          }
        );
      });

      setPermissionStatus('granted');
      setIsRequesting(false);
      return true;
    } catch (err: any) {
      setIsRequesting(false);
      
      if (err.code === err.PERMISSION_DENIED) {
        setPermissionStatus('denied');
        setError('Location permission denied. Please enable location access in your browser settings.');
      } else if (err.code === err.POSITION_UNAVAILABLE) {
        setError('Location information is unavailable.');
        setPermissionStatus('denied');
      } else if (err.code === err.TIMEOUT) {
        setError('Location request timed out. Please try again.');
        setPermissionStatus('prompt');
      } else {
        setError('An error occurred while requesting location permission.');
        setPermissionStatus('prompt');
      }
      
      return false;
    }
  };

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, []);

  return {
    permissionStatus,
    isRequesting,
    error,
    requestPermission,
    checkPermission
  };
};

